/*
 * Tira de subcolecciones que filtra la cuadrícula sin salir de la página.
 *
 * Funcionamiento: al elegir una subcolección se pide en segundo plano la propia
 * página de esa subcolección (Section Rendering API) y se sustituye la cuadrícula.
 * Así el orden y la paginación funcionan de forma nativa.
 * La URL del navegador se queda en la colección padre con ?sub=<handle>.
 *
 * Depende de collection-filters-form.js, que ya sabe pedir y pintar la cuadrícula
 * (se le ha añadido soporte para el parámetro "sub").
 */
class CollectionSubfilter extends HTMLElement {
  constructor() {
    super();
    this.onPopState = () => this.syncActive();
    this.onPaginationClick = (event) => this.handlePaginationClick(event);
  }

  connectedCallback() {
    this.list = this.querySelector('.subfilter__list');
    this.prev = this.querySelector('.subfilter__arrow--prev');
    this.next = this.querySelector('.subfilter__arrow--next');
    this.hasAll = !!this.querySelector('[data-sub=""]');

    this.addEventListener('click', (event) => this.handleClick(event));
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('click', this.onPaginationClick);

    if (this.list) {
      this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    }
    window.addEventListener('resize', () => this.updateArrows());
    if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
    if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

    // Enlace compartido o recarga con ?sub=...
    const sub = this.currentSub();
    if (sub) {
      const search = window.location.search.slice(1);
      history.replaceState({ searchParams: search }, '', window.location.href);
      this.load(search, false);
    }
    this.syncActive();
    this.updateArrows();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('click', this.onPaginationClick);
  }

  /* ---------- Estado ---------- */

  // Subcolección activa según la URL. Si el handle no está en la tira, se ignora.
  currentSub() {
    const sub = new URLSearchParams(window.location.search).get('sub') || '';
    return sub && this.querySelector(`[data-sub="${CSS.escape(sub)}"]`) ? sub : '';
  }

  syncActive() {
    const sub = this.currentSub();
    this.querySelectorAll('[data-sub]').forEach((link) => {
      const active = link.dataset.sub === sub;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });

    const activeLink = this.querySelector('.is-active');
    if (activeLink && this.list) {
      const left = activeLink.parentElement.offsetLeft - (this.list.clientWidth - activeLink.parentElement.offsetWidth) / 2;
      this.list.scrollTo({ left: Math.max(0, left) });
    }
  }

  /* ---------- Eventos ---------- */

  handleClick(event) {
    const link = event.target.closest('a[data-sub]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();

    let sub = link.dataset.sub;
    // Sin botón "Todos", volver a pulsar la activa quita el filtro.
    if (sub === this.currentSub()) {
      if (this.hasAll) return;
      sub = '';
    }

    // Se conserva el orden elegido; la página y el resto se reinician.
    const params = new URLSearchParams();
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.set('sort_by', sort);
    if (sub) params.set('sub', sub);

    // Se marca ya como activa, antes de que llegue la respuesta.
    this.querySelectorAll('[data-sub]').forEach((item) => {
      const active = item.dataset.sub === sub;
      item.classList.toggle('is-active', active);
      if (active) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });

    this.load(params.toString(), true);
  }

  // Con una subcolección activa, la paginación también va por AJAX.
  handlePaginationClick(event) {
    const sub = this.currentSub();
    if (!sub) return;

    const link = event.target.closest('#CollectionProductGrid .pagination a[href]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    if (link.closest('li[disabled]')) return;

    const params = new URL(link.href, window.location.origin).searchParams;
    params.delete('section_id');
    params.delete('sub');
    if (!params.has('sort_by')) {
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      if (sort) params.set('sort_by', sort);
    }
    params.set('sub', sub);

    this.load(params.toString(), true);

    const grid = document.getElementById('CollectionProductGrid');
    if (grid) {
      const top = grid.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  /* ---------- Carga de la cuadrícula ---------- */

  load(searchParams, push = true) {
    const form = document.querySelector('collection-filters-form');
    if (form && typeof form.renderPage === 'function') {
      form.renderPage(searchParams, null, push);
    } else {
      this.fallbackRender(searchParams, push);
    }
  }

  // Solo si la colección no tiene ordenación (y por tanto no hay collection-filters-form).
  async fallbackRender(searchParams, push) {
    const params = new URLSearchParams(searchParams);
    const sub = params.get('sub');
    params.delete('sub');

    const base = sub
      ? window.location.pathname.replace(/\/collections\/[^/]+.*$/, `/collections/${sub}`)
      : window.location.pathname;
    const sectionId = document.getElementById('main-collection-product-grid')?.dataset.id;
    const current = document.getElementById('CollectionProductGrid');
    current?.querySelector('.collection')?.classList.add('loading');

    try {
      const response = await fetch(`${base}?section_id=${sectionId}&${params.toString()}`);
      const html = await response.text();
      const grid = new DOMParser().parseFromString(html, 'text/html').getElementById('CollectionProductGrid');
      if (grid && current) current.innerHTML = grid.innerHTML;
      if (push) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      }
    } catch (error) {
      console.error(error);
      current?.querySelector('.collection')?.classList.remove('loading');
    }
  }

  /* ---------- Flechas ---------- */

  updateArrows() {
    if (!this.list || !this.prev || !this.next) return;
    const max = this.list.scrollWidth - this.list.clientWidth;
    this.prev.hidden = this.list.scrollLeft <= 8;
    this.next.hidden = max <= 8 || this.list.scrollLeft >= max - 8;
  }

  scrollByPage(direction) {
    this.list.scrollBy({ left: direction * this.list.clientWidth * 0.8, behavior: 'smooth' });
  }
}

customElements.define('collection-subfilter', CollectionSubfilter);
